import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const CreateProjectModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState('choose');
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedFramework, setSelectedFramework] = useState('react');

  const templates = [
  {
    id: 'blank',
    name: 'Blank Project',
    description: 'Start from scratch with a clean canvas',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_18482f1d0-1764807099837.png",
    thumbnailAlt: 'Minimalist white canvas with subtle grid lines representing blank project template',
    icon: 'FileText',
    price: 14.99
  },
  {
    id: 'landing',
    name: 'Landing Page',
    description: 'Modern landing page with hero section',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_109d656ea-1766560938446.png",
    thumbnailAlt: 'Modern website landing page mockup with hero section and call-to-action buttons on laptop screen',
    icon: 'Layout',
    price: 14.99
  },
  {
    id: 'ecommerce',
    name: 'E-commerce Store',
    description: 'Full-featured online store template',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1eb7c1cb3-1764655134961.png",
    thumbnailAlt: 'E-commerce website interface showing product grid with shopping cart and checkout elements',
    icon: 'ShoppingCart',
    price: 14.99
  },
  {
    id: 'portfolio',
    name: 'Portfolio',
    description: 'Showcase your work professionally',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1bd315aa1-1764652054746.png",
    thumbnailAlt: 'Creative portfolio website layout displaying project thumbnails in masonry grid format',
    icon: 'Briefcase',
    price: 14.99
  }];


  const handleCreateProject = () => {
    if (!projectName?.trim()) {
      return;
    }

    const newProject = {
      id: Date.now()?.toString(),
      name: projectName?.trim(),
      description: projectDescription?.trim() || 'App Creator Project',
      template: selectedTemplate,
      framework: selectedFramework,
      status: 'active',
      lastModified: new Date()?.toISOString(),
      createdAt: new Date()?.toISOString(),
      progress: 0,
      framework_icon: 'Code',
      price: 14.99
    };

    // Navigate to checkout instead of directly creating project
    navigate('/checkout', { state: { projectData: newProject } });
    onClose();
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
  };

  const handleBack = () => {
    if (step === 'details') {
      setStep('choose');
    }
  };

  const resetModal = () => {
    setStep('choose');
    setProjectName('');
    setProjectDescription('');
    setSelectedTemplate(null);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
          <div>
            <h2 className="font-heading font-semibold text-xl md:text-2xl text-foreground">
              {step === 'choose' ? 'Create New Project' : 'Project Details'}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {step === 'choose' ? 'Choose a template or start from scratch' : 'Enter your project information'}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-250"
            aria-label="Close modal">

            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          {step === 'choose' &&
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {templates?.map((template) =>
            <button
              key={template?.id}
              onClick={() => handleTemplateSelect(template)}
              className={`text-left bg-muted rounded-lg overflow-hidden hover:shadow-md transition-all duration-250 ${
              selectedTemplate?.id === template?.id ?
              'ring-2 ring-primary shadow-md' :
              ''}`
              }>

                  <div className="aspect-video relative overflow-hidden bg-background">
                    <Image
                  src={template?.thumbnail}
                  alt={template?.thumbnailAlt}
                  className="w-full h-full object-cover" />

                    {selectedTemplate?.id === template?.id &&
                <div className="absolute top-3 right-3 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                        <Icon name="Check" size={16} className="text-primary-foreground" />
                      </div>
                }
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name={template?.icon} size={20} className="text-primary" />
                      <h3 className="font-heading font-semibold text-base text-foreground">
                        {template?.name}
                      </h3>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">{template?.description}</p>
                    <div className="flex items-center gap-1 text-primary">
                      <Icon name="DollarSign" size={16} />
                      <span className="font-bold text-lg">{template?.price?.toFixed(2)}</span>
                    </div>
                  </div>
                </button>
            )}
            </div>
          }

          {step === 'details' &&
          <div className="max-w-2xl mx-auto space-y-6">
              <div className="bg-muted rounded-lg p-4 flex items-center gap-3">
                <Icon name={selectedTemplate?.icon} size={24} className="text-primary" />
                <div>
                  <div className="font-medium text-foreground">{selectedTemplate?.name}</div>
                  <div className="text-sm text-muted-foreground">
                    {selectedTemplate?.description}
                  </div>
                </div>
              </div>

              <Input
              label="Project Name"
              type="text"
              placeholder="My Awesome Project"
              value={projectName}
              onChange={(e) => setProjectName(e?.target?.value)}
              required />


              <Input
              label="Description (Optional)"
              type="text"
              placeholder="Brief description of your project"
              value={projectDescription}
              onChange={(e) => setProjectDescription(e?.target?.value)}
              description="Help your team understand the project purpose" />

            </div>
          }
        </div>

        <div className="flex items-center justify-between gap-3 p-4 md:p-6 border-t border-border">
          <Button variant="ghost" onClick={step === 'details' ? handleBack : handleClose}>
            {step === 'details' ? 'Back' : 'Cancel'}
          </Button>
          <div className="flex items-center gap-2">
            {step === 'choose' &&
            <Button
              variant="outline"
              iconName="Layers"
              iconPosition="left"
              onClick={() => navigate('/template-marketplace')}>

                Browse Templates
              </Button>
            }
            <Button
              variant="default"
              iconName={step === 'choose' ? 'ArrowRight' : 'Check'}
              iconPosition="right"
              onClick={handleCreateProject}
              disabled={
              step === 'choose' && !selectedTemplate ||
              step === 'details' && !projectName?.trim()
              }>

              {step === 'choose' ? 'Continue' : 'Create Project'}
            </Button>
          </div>
        </div>
      </div>
    </div>);

};

export default CreateProjectModal;